
/**
 * WhereDestinationPicker
 *
 * A unified "Where" input that replaces the separate city react-select and
 * DestinationPicker. A single search box returns cities, regions/states and
 * countries in one merged list; city results show circular photo thumbnails
 * (same as the old city select), regions and countries show a map-pin icon.
 *
 * On selection it updates both formData.primary_destination and
 * formData.city (for backwards-compat with downstream consumers).
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Image from '@/components/AppImage/AppImage';
import { createPortal } from 'react-dom';
import { getDestinationGeo, getPhotoUrlFromGeo } from '@/api/destinations';
import type { CitySearchResult, PrimaryDestination } from '@/types/city';
import { getFirstPhotoUrl } from '@/utils/photoUtils';
import {
  buildPrimaryDestinationFromAutocompleteResult,
  buildPrimaryDestinationFromCityResult,
  getAutocompleteResultDisplayLabels,
  getCityResultDisplayLabels,
  getCityResultPhotoUrl,
  isCitySearchResult,
  isDestinationSelected,
} from '@/utils/destinationPickerUtils';
import { useDestinationSearch } from '@/hooks/useDestinationSearch';
import type { AnyDestinationResult } from '@/utils/destinationSearchRanking';
import { computeVisualViewportKeyboardInset } from '@/utils/keyboardInset';
import './WhereDestinationPicker.css';

const DROPDOWN_GAP = 4;
const DROPDOWN_MIN_HEIGHT = 120;
const DROPDOWN_MAX_HEIGHT = 320;

// Feature-detect the Popover API once at module load. Top-layer elements always
// paint above every z-index stacking context, including isolation: isolate parents.
const SUPPORTS_POPOVER =
  typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

type DropdownLayout = {
  left: number;
  width: number;
  maxHeight: number;
  placement: 'below' | 'above';
  anchorTop?: number;
  anchorBottom?: number;
};

function readKeyboardInset(): number {
  const cssKb = parseInt(
    document.documentElement.style.getPropertyValue('--keyboard-height') || '0',
    10,
  );
  if (Number.isFinite(cssKb) && cssKb > 0) {
    return cssKb;
  }
  return computeVisualViewportKeyboardInset();
}

function computeDropdownLayout(input: HTMLInputElement): DropdownLayout {
  const rect = input.getBoundingClientRect();
  const keyboardHeight = readKeyboardInset();
  const viewportTop = window.visualViewport?.offsetTop ?? 0;
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const visibleBottom = viewportTop + viewportHeight - keyboardHeight;

  const spaceBelow = visibleBottom - rect.bottom - DROPDOWN_GAP;
  const spaceAbove = rect.top - viewportTop - DROPDOWN_GAP;

  if (spaceBelow >= DROPDOWN_MIN_HEIGHT) {
    return {
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow),
      placement: 'below',
      anchorTop: rect.bottom + DROPDOWN_GAP,
    };
  }

  if (spaceAbove >= DROPDOWN_MIN_HEIGHT) {
    return {
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove),
      placement: 'above',
      anchorBottom: window.innerHeight - rect.top + DROPDOWN_GAP,
    };
  }

  const useAbove = spaceAbove > spaceBelow;
  return {
    left: rect.left,
    width: rect.width,
    maxHeight: Math.max(80, useAbove ? spaceAbove : spaceBelow),
    placement: useAbove ? 'above' : 'below',
    ...(useAbove
      ? { anchorBottom: window.innerHeight - rect.top + DROPDOWN_GAP }
      : { anchorTop: rect.bottom + DROPDOWN_GAP }),
  };
}

export interface WhereDestinationPickerProps {
  value: PrimaryDestination | null;
  onChange: (value: PrimaryDestination | null) => void;
  onCityChange?: (city: CitySearchResult | null) => void;
  onPhotoChange?: (photoUrl: string | undefined) => void;
  disabled?: boolean;
  /** Match Create Trip/List modal typography when embedded in those flows. */
  embedded?: boolean;
  /** Focus the search input when the picker mounts. */
  autoFocus?: boolean;
}

const WhereDestinationPicker: React.FC<WhereDestinationPickerProps> = ({
  value,
  onChange,
  onCityChange,
  onPhotoChange,
  disabled = false,
  embedded = false,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState(value?.identifying_name ?? '');
  const { results, isLoading, search, clear } = useDestinationSearch();
  const [isFetchingGeo, setIsFetchingGeo] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [dropdownLayout, setDropdownLayout] = useState<DropdownLayout | null>(null);

  // Sync the input when value changes externally: edit shows the selection, create has null value.
  useEffect(() => {
    if (value) {
      setQuery(value.identifying_name);
    } else {
      setQuery('');
    }
  }, [value]);

  // Open the dropdown whenever a search yields rows.
  useEffect(() => {
    if (results.length > 0) setIsOpen(true);
  }, [results]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value;
      setQuery(q);
      if (value) {
        onChange(null);
        onCityChange?.(null);
        onPhotoChange?.(undefined);
      }
      search(q);
    },
    [onChange, onCityChange, onPhotoChange, search, value]
  );

  const handleSelect = useCallback(
    async (result: AnyDestinationResult) => {
      clear();
      setIsOpen(false);

      if (isCitySearchResult(result)) {
        const { label } = getCityResultDisplayLabels(result);
        const dest = buildPrimaryDestinationFromCityResult(result);
        setQuery(label || dest.identifying_name);

        onChange(dest);
        onCityChange?.(result);
        const cityPhotoUrl =
          getFirstPhotoUrl(
            { photos: result.photos as Array<{ name?: string; photo_reference?: string }> | undefined },
            720,
          ) || undefined;
        onPhotoChange?.(cityPhotoUrl);

        // Fetch geoposition for map centering
        if (result.google_place_id) {
          setIsFetchingGeo(true);
          try {
            const geo = await getDestinationGeo(result.google_place_id);
            const geoPhotoUrl = getPhotoUrlFromGeo(geo, 720);
            if (geo?.geoposition) {
              onChange({ ...dest, geoposition: geo.geoposition });
            }
            if (!cityPhotoUrl && geoPhotoUrl) {
              onPhotoChange?.(geoPhotoUrl);
            }
          } finally {
            setIsFetchingGeo(false);
          }
        }
      } else {
        const { label } = getAutocompleteResultDisplayLabels(result);
        const dest = buildPrimaryDestinationFromAutocompleteResult(result);
        setQuery(label);

        onChange(dest);
        onCityChange?.(null);
        onPhotoChange?.(undefined);

        if (result.google_place_id) {
          setIsFetchingGeo(true);
          try {
            const geo = await getDestinationGeo(result.google_place_id);
            const geoPhotoUrl = getPhotoUrlFromGeo(geo, 720);
            if (geo?.geoposition) {
              onChange({ ...dest, geoposition: geo.geoposition });
            }
            if (geoPhotoUrl) {
              onPhotoChange?.(geoPhotoUrl);
            }
          } finally {
            setIsFetchingGeo(false);
          }
        }
      }
    },
    [clear, onChange, onCityChange, onPhotoChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    onCityChange?.(null);
    onPhotoChange?.(undefined);
    setQuery('');
    clear();
    setIsOpen(false);
  }, [clear, onChange, onCityChange, onPhotoChange]);

  // Position portal dropdown relative to input, clamped above keyboard inset.
  useLayoutEffect(() => {
    if (!isOpen || !inputRef.current) {
      setDropdownLayout(null);
      return;
    }

    const update = (): void => {
      if (inputRef.current) {
        setDropdownLayout(computeDropdownLayout(inputRef.current));
      }
    };

    update();

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-keyboard-open'],
    });

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [isOpen, results.length]);

  // Close on outside click/tap — but not when the tap merely dismisses the
  // keyboard on iOS, which would hide the dropdown before the user can pick.
  const wasInputFocusedRef = useRef(false);
  useEffect(() => {
    const trackFocus = () => {
      wasInputFocusedRef.current = inputRef.current === document.activeElement;
    };
    document.addEventListener('focusin', trackFocus);
    document.addEventListener('focusout', trackFocus);

    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = (e as TouchEvent).touches
        ? ((e as TouchEvent).touches[0]?.target as Node | null)
        : ((e as MouseEvent).target as Node);
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      const listbox = document.getElementById('where-picker-listbox');
      if (listbox?.contains(target)) return;

      // If the input was focused (keyboard open) and the tap lands inside the
      // modal panel, the user is just dismissing the keyboard — keep dropdown.
      if (wasInputFocusedRef.current) {
        const modalPanel = containerRef.current?.closest('.clm-modal-panel');
        if (modalPanel?.contains(target as Node)) {
          return;
        }
      }

      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('focusin', trackFocus);
      document.removeEventListener('focusout', trackFocus);
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  useEffect(() => {
    if (!autoFocus) return;
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [autoFocus]);

  const showDropdown = isOpen && results.length > 0;
  const showSpinner = isLoading || isFetchingGeo;

  // Drive dropdown visibility via the Popover API (top layer) or a fallback class.
  // Checking the element instance rather than the module constant keeps behaviour
  // consistent with polyfills and makes the logic directly unit-testable.
  useEffect(() => {
    const el = dropdownRef.current;
    if (!el) return;
    const shouldShow = showDropdown && dropdownLayout !== null;
    if (typeof el.showPopover === 'function') {
      try { shouldShow ? el.showPopover() : el.hidePopover(); } catch { /* noop — already in target state */ }
    } else {
      el.classList.toggle('where-picker__dropdown--open', shouldShow);
    }
  }, [showDropdown, dropdownLayout]);

  return (
    <div
      ref={containerRef}
      className={`where-picker${embedded ? ' where-picker--embedded' : ''}`}
    >
      {/* Input row */}
      <div className="where-picker__input-row">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder="Search a city, region, state or country…"
          disabled={disabled}
          role="combobox"
          aria-label="Search for a destination"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="where-destination-picker-results"
          className="where-picker__input"
        />
        {showSpinner && (
          <span className="where-picker__spinner" aria-hidden="true" />
        )}
        {!showSpinner && value && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            aria-label="Clear destination"
            className="where-picker__clear"
          >
            ✕
          </button>
        )}
      </div>

      {/* Portal dropdown — always mounted so the ref is stable for showPopover/hidePopover.
          The <ul> uses popover="manual" to enter the CSS Top Layer (beats all z-index stacking
          contexts). Visibility is driven by the useEffect above. Results are only rendered
          when open to avoid unnecessary DOM weight. */}
      {typeof document !== 'undefined' &&
        createPortal(
          <ul
            ref={dropdownRef}
            popover="manual"
            id="where-picker-listbox"
            role="listbox"
            aria-label="Destination suggestions"
            className={`where-picker__dropdown${
              dropdownLayout?.placement === 'above' ? ' where-picker__dropdown--above' : ''
            }`}
            style={dropdownLayout ? {
              left: dropdownLayout.left,
              width: Math.max(dropdownLayout.width, 280),
              maxHeight: dropdownLayout.maxHeight,
              ...(dropdownLayout.placement === 'below'
                ? { top: dropdownLayout.anchorTop }
                : { bottom: dropdownLayout.anchorBottom }),
            } : {}}
          >
            {showDropdown ? results.map((result, idx) => {
              const { label, subLabel } = isCitySearchResult(result)
                ? getCityResultDisplayLabels(result)
                : getAutocompleteResultDisplayLabels(result);
              const photo = isCitySearchResult(result) ? getCityResultPhotoUrl(result) : undefined;

              return (
                <li
                  key={`${label}-${idx}`}
                  role="option"
                  aria-selected={isDestinationSelected(value, result)}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(result)}
                    className="where-picker__option"
                  >
                    <span className="where-picker__option-thumb">
                      {photo ? (
                        <div style={{ position: "relative" }} className="where-picker__option-img">
                          <Image
                            src={photo}
                            alt={label}
                            fill
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      ) : (
                        <span className="where-picker__option-icon" aria-hidden="true">
                          📍
                        </span>
                      )}
                    </span>
                    <span className="where-picker__option-text">
                      <span className="where-picker__option-label">{label}</span>
                      {subLabel && (
                        <span className="where-picker__option-sublabel">{subLabel}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            }) : null}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default WhereDestinationPicker;
